/**
 * Cancelling a user's requirement to sign in with 2FA. Another user — one
 * with 2FA of their own — has to verify the request by email before it takes
 * effect, so an admin picks that verifier here and the API emails them.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface RecoveryUser {
  id: string
  username: string
  displayName?: string
}

export const TwoFactorRecoveryApi = {
  /** The users who could verify a recovery for this user. */
  getVerifiers: async (userId: string): Promise<RecoveryUser[]> => {
    const res = await apiFetch(
      `${API_BASE}/2fa/recovery/verifiers/${encodeURIComponent(userId)}?context=${getContext()}`,
      { headers: JSON_HEADERS }
    )
    if (!res.ok) await throwApiError(res, 'Failed to fetch verifying users')
    return toArray<RecoveryUser>(await res.json())
  },

  /** The users already asked to verify a recovery for this user. */
  getVerifying: async (userId: string): Promise<RecoveryUser[]> => {
    const res = await apiFetch(`${API_BASE}/2fa/recovery/verifying/${encodeURIComponent(userId)}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch verifying users')
    return toArray<RecoveryUser>(await res.json())
  },

  /** Emails the verifier, asking them to confirm the recovery. */
  initiate: async (userId: string, verifyUserId: string): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/2fa/recovery/initiate?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ recoveryUser: userId, verifyUser: verifyUserId }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to start 2FA recovery')
  },
}
